import { Obj, InputEvents, Metrics, ObjectState, SurfaceMapping, Surface, UnresolvedObj } from "./types";
import { recursivelyUpdateObject, moveObject, createSprite, moveObjectAbsolute } from "./core";

type Vector4D = [number, number, number, number]; // [x, y, z, v]

// TODO need a tool to investigate the state of the object tree

const createSurfaceMapping = () => {
  const cols = 2 ** 8; // 2 ** 7
  const width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
  const height = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)
  const rows = Math.floor(height / width * cols);
  const pixelW = width / cols;
  const pixelH = pixelW;

  return { width, height, pixelW, pixelH, rows, cols };
}

const createCanvasSurface = (canvasId: string, { width, height, pixelW, pixelH, cols, rows }: SurfaceMapping) => {
  const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
  if (!canvas) {
    throw Error("Couldn't find canvas.");
  }
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw Error("Couldn't get canvas context.");
  }

  ctx.imageSmoothingEnabled = false;

  const dpr = window.devicePixelRatio ?? 1;
  ctx.scale(dpr, dpr);

  return {
    resetTransform: ctx.resetTransform.bind(ctx),
    // resetTransform: () => ctx.resetTransform(),
    clear: () => ctx.clearRect(0, 0, width, height),
    setFillStyle: (style: string) => ctx.fillStyle = style,
    fillRect: (x: number, y: number) => ctx.fillRect(x * pixelW, y * pixelH, pixelW, pixelH),
    width, height, pixelW, pixelH, cols, rows
  }
}

// # Game Loop

const renderFrame = (surface: Surface, frame: Vector4D[]) => {
  // clear & reset
  // TODO try ctx.reset() instead of the following two
  surface.resetTransform();
  surface.clear();

  frame.forEach(([x, y, z, v]) => {
    surface.setFillStyle((v > 0) ? "black" : "white");
    surface.fillRect(x, y);
  });
}

const flattenFramesByZIndex = (frames: Vector4D[]) => {
  return Object.values(
    frames.reduce((acc: Record<string, Vector4D>, [x, y, z, v]) => {
      const key = `${x},${y}`;
      const c = acc[key];
      if ((!c || z > c[2]) && v >= 0) {
        acc[key] = [x, y, z, v];
      }
      return acc;
    }, {})
  );
}

// Remove pixels out of bounds
const filterOutOfBounds = (frames: Vector4D[], rows: number, cols: number) => {
  return frames.filter(([x, y]) => {
    return x >= 0 && x < cols && y >= 0 && y < rows;
  });
}

// Remove pixels with value < 0 (invisible)
const filterInvisiblePixels = (frames: Vector4D[]) => {
  return frames.filter((v) => {
    return v[3] >= 0;
  });
}

// Get frame
const getFrameFromObject = (obj: Obj) => {
  return obj.sprite.c.reduce((acc: Vector4D[], v, i) => {
    const localX = i % obj.sprite.w;
    const localY = Math.floor(i / obj.sprite.w);
    const x = obj.x + localX;
    const y = obj.y + localY;
    acc.push([x, y, obj.z, v]);
    return acc;
  }, []);
}

// Call stack danger
// Recursively get frames from object and its children
// Recursively apply private functions to the object
const getFrame = (obj: Obj) => {
  let frames: Vector4D[] = [];
  const fn = (o: Obj) => {
    const f = getFrameFromObject(o);
    frames = frames.concat(f);
  };
  recursivelyUpdateObject(obj, fn);
  return frames;
}

// Recursively step object and its children
// Recursively call public functions on the object
const stepObject = (obj: Obj, startTime: number, timestamp: number, inputEvents: InputEvents, metrics: Metrics, inFocusObject: Obj | null, objectState: ObjectState, surface: Surface) => {
  const fn = (o: Obj) => {
    if (o.step) {
      const inFocus = o === inFocusObject;
      o.step(startTime, timestamp, inputEvents, metrics, inFocus, objectState, surface, o);
    }
  };
  recursivelyUpdateObject(obj, fn);
}

const createInputEvents = (surfaceMapping: SurfaceMapping): InputEvents => {
  let mouseX = 0;
  let mouseY = 0;

  addEventListener("mousemove", (event) => {
    mouseX = Math.floor(event.clientX / surfaceMapping.pixelW);
    mouseY = Math.floor(event.clientY / surfaceMapping.pixelH);
  });

  // Please capture two distinct events for a single click and a click-and-hold (or drag) action.
  let mouseDown = false;
  let clickDetected = false;
  let dragDetected = false;
  let dragThreshold = 5;
  let dragStartX = 0;
  let dragStartY = 0;

  addEventListener("mousedown", (event) => {
    mouseDown = true;
    clickDetected = false;
    dragDetected = false;
    dragStartX = event.clientX;
    dragStartY = event.clientY;
  });

  addEventListener("mouseup", (event) => {
    if (!dragDetected) {
      clickDetected = true;
    }
    mouseDown = false;
    dragDetected = false;
  });

  addEventListener("mousemove", (event) => {
    if (mouseDown && !dragDetected) {
      const dx = event.clientX - dragStartX;
      const dy = event.clientY - dragStartY;
      if (Math.sqrt(dx * dx + dy * dy) > dragThreshold) {
        dragDetected = true;
      }
    }
  });

  return {
    get mouseX() {
      return mouseX;
    },
    get mouseY() {
      return mouseY;
    },
    get clickDetected() {
      return clickDetected;
    },
    get dragDetected() {
      return dragDetected;
    },
    get dragStartX() {
      return Math.floor(dragStartX / surfaceMapping.pixelW); // calc once in the handler
    },
    get dragStartY() {
      return Math.floor(dragStartY / surfaceMapping.pixelH); // calc once in the handler
    },
    step() {
      clickDetected = false;
    }
  }
}

const createMetrics = (): Metrics => {
  let lastFrameTime = 0;
  let frameCount = 0;
  let fps = 0;

  const incFrame = (timestamp: number) => {
    frameCount++;

    if (lastFrameTime === 0) {
      lastFrameTime = timestamp;
      return;
    }

    const deltaTime = timestamp - lastFrameTime;
    // Log FPS every second
    if (deltaTime >= 1000) {
      fps = frameCount / (deltaTime / 1000);
      frameCount = 0;
      lastFrameTime = timestamp;
    }
  };
  return {
    get fps() {
      return fps;
    },
    incFrame,
  }

}

// Based on mouse events and object positions, determine which object is in focus. Not stateful.
const getInFocusObjects = (
  obj: Obj,
  inputEvents: InputEvents,
  currentZ: number = -Infinity,
  currentDraggableZ: number = -Infinity
) => {
  let inFocusObject: Obj | null = null;
  let inFocusDraggable: Obj | null = null;

  const isUnderMouse =
    inputEvents.mouseX >= obj.x &&
    inputEvents.mouseX <= obj.x + (obj.w ?? obj.sprite.w) &&
    inputEvents.mouseY >= obj.y &&
    inputEvents.mouseY <= obj.y + (obj.h ?? obj.sprite.h) &&
    obj.z < Infinity; // Ignore things like cursors where z=Infinity

  if (isUnderMouse) {
    if (obj.z >= currentZ) {
      inFocusObject = obj;
      currentZ = obj.z;
    }
    if (obj.isDraggable && obj.z >= currentDraggableZ) {
      inFocusDraggable = obj;
      currentDraggableZ = obj.z;
    }
  }

  if (obj.children) {
    obj.children.forEach((child) => {
      const result = getInFocusObjects(child, inputEvents, currentZ, currentDraggableZ);
      if (result.inFocusObject && result.inFocusObject.z >= (inFocusObject?.z ?? -Infinity)) {
        inFocusObject = result.inFocusObject;
        currentZ = inFocusObject.z;
      }
      if (
        result.inFocusDraggable &&
        result.inFocusDraggable.z >= (inFocusDraggable?.z ?? -Infinity)
      ) {
        inFocusDraggable = result.inFocusDraggable;
        currentDraggableZ = inFocusDraggable.z;
      }
    });
  }

  // console.log(inFocusObject);

  return { inFocusObject, inFocusDraggable };
};

type InFocusState = {
  step: (inputEvents: InputEvents, inFocusObject: Obj | null, inFocusDraggable: Obj | null) => void;
  draggingObject: Obj | null;
}

// "sample" and hold dragDetected and inFocusObject
const createInFocusState = (obj: Obj, inputEvents: InputEvents): InFocusState => {
  let dragActive = false;
  let draggingObject: Obj | null = null;

  const step = (inputEvents: InputEvents, inFocusObject: Obj | null, inFocusDraggable: Obj | null) => {
    if (inputEvents.dragDetected) {
      if (!dragActive) {
        dragActive = true;
        draggingObject = inFocusDraggable
      }
    } else {
      // reset
      dragActive = false;
      draggingObject = null;
    }
  }

  return {
    step,
    get draggingObject() {
      return draggingObject;
    }
  }
}


type Draggable = {
  step: (obj: Obj, surface: Surface, inputEvents: InputEvents, inFocusObject: Obj | null) => void;
}

const createDraggable = () => {
  let prevMouseX = 0;
  let prevMouseY = 0;
  let dx = 0;
  let dy = 0;
  let dragStarted = false;
  let prevObj = null;
  const zIncDefault = 100;

  // Handle drag logic
  const step = (obj: Obj, surface: Surface, inputEvents: InputEvents, inFocusObject: Obj | null) => {
    if (inputEvents.dragDetected) {
      if (!prevMouseX && !prevMouseY) {
        prevMouseX = inputEvents.dragStartX;
        prevMouseY = inputEvents.dragStartY;
      }

      dx = inputEvents.mouseX - prevMouseX;
      dy = inputEvents.mouseY - prevMouseY;

      prevMouseX = inputEvents.mouseX;
      prevMouseY = inputEvents.mouseY;

      if (inFocusObject && inFocusObject.isDraggable) {
        let zInc = 0;
        // Only execute once per drag event
        if (!dragStarted) {
          dragStarted = true;
          prevObj = inFocusObject;
          zInc = zIncDefault;
          console.log("raising Z", zInc, inFocusObject.id);
        }
        moveObject(inFocusObject, dx, dy, zInc);
      }
    } else {
      // reset
      prevMouseX = 0;
      prevMouseY = 0;
      dx = 0;
      dy = 0;
      dragStarted = false;
      if (prevObj) {
        console.log("lowering Z", -zIncDefault, prevObj.id);
        moveObject(prevObj, 0, 0, -zIncDefault);
        prevObj = null;
      }
    }
  }

  return {
    step,
  }
}

// @deprecated
const resolve = (node: UnresolvedObj, parent: Obj): Obj => {

  // Node position is relative to parent position
  const x1 = (node.x ?? 0) + parent.x;
  const y1 = (node.y ?? 0) + parent.y;
  const z1 = (node.z ?? 0) + parent.z;

  // Node width/height overrides parent width/height
  const w1 = node.w ? node.w : parent.w;
  const h1 = node.h ? node.h : parent.h;

  const sprite = node.sprite && node.sprite.w === w1 && node.sprite.h === h1 ? node.sprite : createSprite({ w: w1, h: h1 });

  const newNode = {
    ...node,
    x: x1,
    y: y1,
    z: z1,
    w: w1,
    h: h1,
    sprite,
  };

  if (node.children && node.children.length > 0) {
    // Layout children
    const m = node.m ?? 0;
    const ml = m;
    const mt = m;
    const mr = m;
    const mb = m;

    // Inner children box
    const x2 = x1 + ml;
    const y2 = y1 + mt;
    const w2 = w1 - ml - mr;
    const h2 = h1 - mt - mb;

    const nChildren = node.children.length;

    // Default to "column" if not specified
    const direction = node.direction ?? "column";
    if (direction === "column") {
      const x = x2;
      const z = z1;
      const w = w2;

      const sumManualH = node.children.reduce((acc, child) => acc + (child.h ?? 0), 0);
      const nAutoHChildren = node.children.filter((child) => !child.h).length;
      // TODO improve negative condition controls
      if (sumManualH > h2) {
        throw new Error(`Children total height ${sumManualH} exceeds parent height ${h2}`);
      }
      const autoH = Math.floor((h2 - sumManualH) / nAutoHChildren);

      let accH = 0;
      const children = node.children.map((child) => {
        const y = y2 + accH;
        // allow child to override width/height
        const w3 = child.w ? child.w : w;
        const h3 = child.h ? child.h : autoH;
        const sprite = child.sprite && child.sprite.w === w3 && child.sprite.h === h3 ? child.sprite : createSprite({ w: w3, h: h3 });
        const newChild = {
          ...child,
          x: (child.x ?? 0) + x,
          y: (child.y ?? 0) + y,
          z: (child.z ?? 0) + z,
          w: w3,
          h: h3,
          sprite,
        };
        accH += h3;
        return resolve(newChild, { x: 0, y: 0, z: 0, w, h: h2 });
      });

      return { ...newNode, children };
    } else if (direction === "row") {
      const y = y2;
      const z = z1;
      const h = h2;

      const sumManualW = node.children.reduce((acc, child) => acc + (child.w ?? 0), 0);
      const nAutoWChildren = node.children.filter((child) => !child.w).length;
      // TODO improve negative condition controls
      if (sumManualW > w2) {
        throw new Error(`Children total width ${sumManualW} exceeds parent width ${w2}`);
      }
      const autoW = Math.floor((w2 - sumManualW) / nAutoWChildren);

      let accW = 0;
      const children = node.children.map((child, i) => {
        const x = x2 + accW;
        // allow child to override width/height
        const w3 = child.w ? child.w : autoW;
        const h3 = child.h ? child.h : h;
        const sprite = child.sprite && child.sprite.w === w3 && child.sprite.h === h3 ? child.sprite : createSprite({ w: w3, h: h3 });
        const newChild = {
          ...child,
          x: (child.x ?? 0) + x,
          y: (child.y ?? 0) + y,
          z: (child.z ?? 0) + z,
          w: w3,
          h: h3,
          sprite,
        };
        accW += w3;
        return resolve(newChild, { x: 0, y: 0, z: 0, w: w2, h });
      });

      return { ...newNode, children };
    } else {
      throw new Error(`Unknown box direction: ${direction}`);
    }
  } else {
    return newNode; // TODO complaining about unresolved children
  }
};

// @deprecated
const resolveRoot = (rootObj: UnresolvedObj, surfaceMapping: SurfaceMapping): Obj => {
  const w = surfaceMapping.cols;
  const h = surfaceMapping.rows;
  const sprite = createSprite({ w, h });
  const rootVirtualParent = { x: 0, y: 0, z: 0, w, h, m: 0, sprite, id: "$" };
  return resolve(rootObj, rootVirtualParent);
};

const createObjectState = (obj: Obj): ObjectState => {
  // recursively search for o and remove it from children
  const remove = (target: Obj, source: Obj = obj) => {
    if (source.children) {
      const idx = source.children.indexOf(target);
      if (idx !== -1) {
        source.children.splice(idx, 1);
      } else if (target.id) {
        const child = source.children.find((c) => c.id === target.id);
        if (child) {
          source.children.splice(source.children.indexOf(child), 1);
        }
      } else {
        source.children.forEach((child) => {
          remove(target, child);
        });
      }
    }
  }

  const add = (target: Obj) => {
    obj.children?.push(target);
  }

  // @deprecated
  const resolveChildren = () => {
    if (obj.children) {
      obj.children = obj.children.map((child) => resolve(child, obj));
    }
  }

  return {
    remove,
    add,
    resolveChildren,
  }
}

const createGameLoop = (surface: Surface, obj: Obj, inputEvents: InputEvents, metrics: Metrics, draggable: Draggable, objectState: ObjectState, inFocusState: InFocusState) => {
  const startTime = performance.now();

  const step = (timestamp: number) => {
    // Update metrics
    metrics.incFrame(timestamp); // TODO consider renaming to "step"

    // Update object state
    // TODO need to maintain state somewhere to not mutate inFocusDraggable during a drag event
    const { inFocusObject, inFocusDraggable } = getInFocusObjects(obj, inputEvents);

    inFocusState.step(inputEvents, inFocusObject, inFocusDraggable);

    stepObject(obj, startTime, timestamp, inputEvents, metrics, inFocusObject, objectState, surface);

    // Handle click-and-drag
    draggable.step(obj, surface, inputEvents, inFocusState.draggingObject);

    // Render the frame
    let frame = getFrame(obj);
    frame = filterOutOfBounds(frame, surface.rows, surface.cols);
    frame = filterInvisiblePixels(frame);
    frame = flattenFramesByZIndex(frame);
    renderFrame(surface, frame);

    // Reset stateful input events
    inputEvents.step();

    // Loop
    requestAnimationFrame(step);
  };

  requestAnimationFrame(step);
}

// # Public API

export const createCanvasGameLoop = (canvasId: string, createObj: (surfaceMapping: SurfaceMapping) => Obj) => {
  const surfaceMapping = createSurfaceMapping();
  const surface = createCanvasSurface(canvasId, surfaceMapping);
  const inputEvents = createInputEvents(surfaceMapping);
  const metrics = createMetrics();
  const draggable = createDraggable();
  const obj = createObj(surfaceMapping);
  const objectState = createObjectState(obj);
  const inFocusState = createInFocusState(obj, inputEvents);

  createGameLoop(
    surface,
    obj,
    inputEvents,
    metrics,
    draggable,
    objectState,
    inFocusState
  );
}
